import { router } from '@inertiajs/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import AppTheme from '../../theme/AppTheme';
import AppAppBar from '../Landing/components/AppAppBar';

export default function New({ source, email, errors = {} }) {
  const [formData, setFormData] = React.useState({
    name: '',
    email: email || '',
    company_name: '',
    phone: '',
    message: '',
    source: source || ''
  });
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const getPlaceholderBySource = (source) => {
    switch (source) {
      case 'basic_plan':
        return 'Conte-nos sobre sua frota de até 50 veículos e como podemos ajudar você a otimizar suas operações';
      case 'pro_plan':
        return 'Conte-nos sobre sua frota de até 100 veículos e como podemos ajudar você a otimizar suas operações';
      case 'enterprise_plan':
        return 'Conte-nos sobre sua frota e suas necessidades específicas para uma solução personalizada';
      default:
        return 'Conte-nos um pouco sobre sua frota e como podemos ajudar você a otimizar suas operações';
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Nome da empresa é obrigatório';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      router.post('/leads', {
        lead: formData
      }, {
        preserveScroll: true,
        onError: (errors) => {
          setFormErrors(errors);
        },
        onFinish: () => {
          setLoading(false);
        }
      });
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <AppAppBar showNavigation />

      <Container maxWidth="sm">
        <Box sx={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          py: 8,
          mt: 8
        }}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                  Entre em contato
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Ficaremos felizes em ajudar você a encontrar a melhor solução para sua frota
                </Typography>
              </Box>

              {getValidErrors().length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="body2" component="div">
                    <strong>Foram encontrados {getValidErrors().length} erro(s):</strong>
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    {getValidErrors().map((error, index) => (
                      <Box component="li" key={index}>
                        <Typography variant="body2">{error}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    placeholder="Seu nome completo"
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                  />

                  <TextField
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="seu@email.com"
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                  />

                  <TextField
                    id="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange('company_name')}
                    placeholder="Nome da sua empresa"
                    error={!!formErrors.company_name}
                    helperText={formErrors.company_name}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                  />

                  <TextField
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    placeholder="(00) 00000-0000"
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                  />

                  <TextField
                    id="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange('message')}
                    placeholder={getPlaceholderBySource(source)}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    disabled={loading}
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: '100px',
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      mt: 2,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontSize: '1rem'
                    }}
                  >
                    {loading ? 'Enviando...' : 'Enviar mensagem'}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Ao enviar este formulário, você concorda com nossa{' '}
                  <Box
                    component="a"
                    href="/privacy"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Política de Privacidade
                  </Box>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </AppTheme>
  );
} 